import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { AppLayout } from '../layouts/AppLayout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Users, Building2, FileText, Shield, BarChart3, FileCheck, ClipboardList,
  UserPlus, UserMinus, Key, RefreshCw, Trash2, MoreVertical, Search,
  Download, AlertTriangle, CheckCircle, Clock, TrendingUp, Eye, Lock,
  MessageSquare, Phone, Mail, User, Send, X
} from 'lucide-react';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, AreaChart, Area
} from 'recharts';

const ROLES = [
  { value: 'GESTOR_GERAL', label: 'Gestor Geral' },
  { value: 'CONTROLADORIA_INTERNA', label: 'Controladoria Interna' },
  { value: 'PROCURADORIA_JURIDICO', label: 'Procuradoria / Jurídico' },
  { value: 'SECRETARIA_EXECUTORA', label: 'Secretaria Executora' },
  { value: 'OBSERVADOR', label: 'Observador (Governador/Casa Civil)' },
  { value: 'TI_INSTITUCIONAL', label: 'TI Institucional' },
  { value: 'ADMIN_ESTADUAL', label: 'Administrador Estadual' }
];

const UNIT_TYPES = [
  { value: 'SECRETARIA', label: 'Secretaria' },
  { value: 'SUBSECRETARIA', label: 'Subsecretaria' },
  { value: 'DIRETORIA', label: 'Diretoria' },
  { value: 'UNIDADE', label: 'Unidade Interna' }
];

export const AdminGovernoPage = () => {
  const { api, user } = useAuth();
  const [activeTab, setActiveTab] = useState('users');
  const [users, setUsers] = useState([]);
  const [units, setUnits] = useState([]);
  const [riskPanel, setRiskPanel] = useState(null);
  const [accessSummary, setAccessSummary] = useState(null);
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showCreateUnit, setShowCreateUnit] = useState(false);
  const [newUser, setNewUser] = useState({ email: '', name: '', role: '', department: '' });
  const [newUnit, setNewUnit] = useState({ name: '', type: '', responsible_name: '', responsible_email: '', responsible_phone: '', responsible_cargo: '' });
  const [selectedUnit, setSelectedUnit] = useState(null);
  const [showUnitDetail, setShowUnitDetail] = useState(false);
  const [showUnitChat, setShowUnitChat] = useState(false);
  const [chatMessage, setChatMessage] = useState('');
  const [chatMessages, setChatMessages] = useState([]);

  useEffect(() => {
    fetchData();
  }, [activeTab]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'users') {
        const res = await api.get('/admin/users');
        setUsers(res.data);
      } else if (activeTab === 'structure') {
        const res = await api.get('/admin/structure/units');
        setUnits(res.data);
      } else if (activeTab === 'risk') {
        const res = await api.get('/admin/dashboard/risk-panel');
        setRiskPanel(res.data);
      } else if (activeTab === 'access') {
        const res = await api.get('/admin/access/summary');
        setAccessSummary(res.data);
      } else if (activeTab === 'audit') {
        const res = await api.get('/admin/audit-logs?limit=100');
        setAuditLogs(res.data);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateUser = async () => {
    try {
      const res = await api.post('/admin/users', newUser);
      toast.success(`Usuário criado! Senha temporária: ${res.data.temp_password}`);
      setShowCreateUser(false);
      setNewUser({ email: '', name: '', role: '', department: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar usuário');
    }
  };

  const handleUserAction = async (userId, action) => {
    try {
      if (action === 'suspend') {
        await api.post(`/admin/users/${userId}/suspend`);
        toast.success('Usuário suspenso');
      } else if (action === 'reactivate') {
        await api.post(`/admin/users/${userId}/reactivate`);
        toast.success('Usuário reativado');
      } else if (action === 'reset-password') {
        const res = await api.post(`/admin/users/${userId}/reset-password`);
        toast.success(`Nova senha: ${res.data.new_password}`);
      } else if (action === 'delete') {
        await api.delete(`/admin/users/${userId}`);
        toast.success('Usuário excluído');
      } else if (action === 'toggle-2fa') {
        await api.post(`/admin/users/${userId}/toggle-2fa?enable=true`);
        toast.success('2FA ativado');
      }
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro na operação');
    }
  };

  const handleCreateUnit = async () => {
    try {
      await api.post('/admin/structure/units', newUnit);
      toast.success('Unidade criada com sucesso');
      setShowCreateUnit(false);
      setNewUnit({ name: '', type: '', responsible_name: '', responsible_email: '', responsible_phone: '', responsible_cargo: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao criar unidade');
    }
  };

  const handleOpenUnitDetail = (unit) => {
    setSelectedUnit(unit);
    setShowUnitDetail(true);
  };

  const handleOpenChat = async (unit) => {
    setSelectedUnit(unit);
    setChatMessages([]);
    // Fetch previous messages
    try {
      const res = await api.get(`/admin/unit-chat/${unit.unit_id}/messages`);
      setChatMessages(res.data || []);
    } catch (e) {
      // No messages yet
    }
    setShowUnitChat(true);
  };

  const handleSendMessage = async () => {
    if (!chatMessage.trim() || !selectedUnit) return;
    try {
      await api.post(`/admin/unit-chat/${selectedUnit.unit_id}/messages`, {
        message: chatMessage,
        recipient_unit_id: selectedUnit.unit_id
      });
      setChatMessages([...chatMessages, {
        sender_name: user?.name || 'Você',
        message: chatMessage,
        created_at: new Date().toISOString()
      }]);
      setChatMessage('');
      toast.success('Mensagem enviada');
    } catch (e) {
      toast.error('Erro ao enviar mensagem');
    }
  };

  const handleGenerateReport = async (type) => {
    try {
      toast.info('Gerando relatório PDF...');
      const response = await api.get(`/admin/reports/${type}/pdf`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `relatorio_${type}_${new Date().toISOString().split('T')[0]}.pdf`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      a.remove();
      toast.success('PDF baixado com sucesso');
    } catch (error) {
      toast.error('Erro ao gerar relatório PDF');
      console.error(error);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const COLORS = ['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'];

  return (
    <AppLayout>
      <div className="space-y-6" data-testid="admin-governo-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-slate-900">Administração do Órgão</h1>
            <p className="text-slate-500 mt-1">Gerencie usuários, estrutura e controle institucional</p>
          </div>
          <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-sm px-4 py-2">
            <Shield className="w-4 h-4 mr-2" />
            Admin Governo
          </Badge>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-slate-100 p-1 rounded-xl">
            <TabsTrigger value="users" className="rounded-lg data-[state=active]:bg-white">
              <Users className="w-4 h-4 mr-2" />Usuários
            </TabsTrigger>
            <TabsTrigger value="structure" className="rounded-lg data-[state=active]:bg-white">
              <Building2 className="w-4 h-4 mr-2" />Estrutura
            </TabsTrigger>
            <TabsTrigger value="risk" className="rounded-lg data-[state=active]:bg-white">
              <BarChart3 className="w-4 h-4 mr-2" />Painel de Risco
            </TabsTrigger>
            <TabsTrigger value="reports" className="rounded-lg data-[state=active]:bg-white">
              <FileCheck className="w-4 h-4 mr-2" />Relatórios
            </TabsTrigger>
            <TabsTrigger value="audit" className="rounded-lg data-[state=active]:bg-white">
              <ClipboardList className="w-4 h-4 mr-2" />Auditoria
            </TabsTrigger>
            <TabsTrigger value="access" className="rounded-lg data-[state=active]:bg-white">
              <Eye className="w-4 h-4 mr-2" />Acessos
            </TabsTrigger>
          </TabsList>

          {/* USERS TAB */}
          <TabsContent value="users" className="space-y-4">
            <div className="flex items-center justify-between gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar usuários..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <Dialog open={showCreateUser} onOpenChange={setShowCreateUser}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <UserPlus className="w-4 h-4 mr-2" />Criar Usuário
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Novo Usuário</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Input
                      placeholder="Nome completo"
                      value={newUser.name}
                      onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    />
                    <Input
                      placeholder="E-mail"
                      type="email"
                      value={newUser.email}
                      onChange={(e) => setNewUser({ ...newUser, email: e.target.value })}
                    />
                    <Select onValueChange={(v) => setNewUser({ ...newUser, role: v })}>
                      <SelectTrigger><SelectValue placeholder="Selecione o papel" /></SelectTrigger>
                      <SelectContent>
                        {ROLES.map(r => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Departamento (opcional)"
                      value={newUser.department}
                      onChange={(e) => setNewUser({ ...newUser, department: e.target.value })}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateUser(false)}>Cancelar</Button>
                    <Button onClick={handleCreateUser}>Criar Usuário</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <Card>
              <CardContent className="p-0">
                <table className="w-full">
                  <thead className="bg-slate-50 border-b">
                    <tr>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Usuário</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Papel</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Status</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">Último Acesso</th>
                      <th className="text-left p-4 text-sm font-semibold text-slate-600">2FA</th>
                      <th className="text-right p-4 text-sm font-semibold text-slate-600">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((u) => (
                      <tr key={u.user_id} className="border-b hover:bg-slate-50">
                        <td className="p-4">
                          <div>
                            <p className="font-medium text-slate-900">{u.name}</p>
                            <p className="text-sm text-slate-500">{u.email}</p>
                          </div>
                        </td>
                        <td className="p-4">
                          <Badge variant="outline">{ROLES.find(r => r.value === u.role)?.label || u.role}</Badge>
                        </td>
                        <td className="p-4">
                          {u.is_active ? (
                            <Badge className="bg-green-50 text-green-700">Ativo</Badge>
                          ) : (
                            <Badge className="bg-red-50 text-red-700">Inativo</Badge>
                          )}
                        </td>
                        <td className="p-4 text-sm text-slate-500">
                          {u.last_access ? new Date(u.last_access).toLocaleString('pt-BR') : (
                            <span className="text-amber-600">Nunca acessou</span>
                          )}
                        </td>
                        <td className="p-4">
                          {u.two_factor_enabled ? (
                            <Lock className="w-4 h-4 text-green-600" />
                          ) : (
                            <span className="text-slate-300">-</span>
                          )}
                        </td>
                        <td className="p-4 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button size="sm" variant="ghost" onClick={() => handleUserAction(u.user_id, 'reset-password')}>
                              <Key className="w-4 h-4" />
                            </Button>
                            {u.is_active ? (
                              <Button size="sm" variant="ghost" onClick={() => handleUserAction(u.user_id, 'suspend')}>
                                <UserMinus className="w-4 h-4 text-amber-600" />
                              </Button>
                            ) : (
                              <Button size="sm" variant="ghost" onClick={() => handleUserAction(u.user_id, 'reactivate')}>
                                <RefreshCw className="w-4 h-4 text-green-600" />
                              </Button>
                            )}
                            <Button size="sm" variant="ghost" onClick={() => handleUserAction(u.user_id, 'toggle-2fa')}>
                              <Lock className="w-4 h-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => handleUserAction(u.user_id, 'delete')}>
                              <Trash2 className="w-4 h-4 text-red-600" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STRUCTURE TAB */}
          <TabsContent value="structure" className="space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-semibold">Estrutura Institucional</h2>
              <Dialog open={showCreateUnit} onOpenChange={setShowCreateUnit}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-600 hover:bg-blue-700">
                    <Building2 className="w-4 h-4 mr-2" />Nova Unidade
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Criar Unidade Institucional</DialogTitle>
                  </DialogHeader>
                  <div className="space-y-4 py-4">
                    <Input
                      placeholder="Nome da unidade"
                      value={newUnit.name}
                      onChange={(e) => setNewUnit({ ...newUnit, name: e.target.value })}
                    />
                    <Select onValueChange={(v) => setNewUnit({ ...newUnit, type: v })}>
                      <SelectTrigger><SelectValue placeholder="Tipo de unidade" /></SelectTrigger>
                      <SelectContent>
                        {UNIT_TYPES.map(t => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                    <Input
                      placeholder="Nome do responsável"
                      value={newUnit.responsible_name}
                      onChange={(e) => setNewUnit({ ...newUnit, responsible_name: e.target.value })}
                    />
                    <Input
                      placeholder="Cargo do responsável"
                      value={newUnit.responsible_cargo}
                      onChange={(e) => setNewUnit({ ...newUnit, responsible_cargo: e.target.value })}
                    />
                    <Input
                      placeholder="E-mail do responsável"
                      type="email"
                      value={newUnit.responsible_email}
                      onChange={(e) => setNewUnit({ ...newUnit, responsible_email: e.target.value })}
                    />
                    <Input
                      placeholder="Telefone do responsável"
                      value={newUnit.responsible_phone}
                      onChange={(e) => setNewUnit({ ...newUnit, responsible_phone: e.target.value })}
                    />
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setShowCreateUnit(false)}>Cancelar</Button>
                    <Button onClick={handleCreateUnit}>Criar Unidade</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {units.map((unit) => (
                <Card 
                  key={unit.unit_id} 
                  className="hover:shadow-lg transition-all cursor-pointer hover:border-blue-300 hover:scale-[1.02]"
                  onClick={() => handleOpenUnitDetail(unit)}
                >
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <Badge className="mb-2">{unit.type}</Badge>
                        <h3 className="font-semibold text-slate-900">{unit.name}</h3>
                        {unit.responsible_name && (
                          <p className="text-sm text-slate-500 mt-2">
                            <User className="w-3 h-3 inline mr-1" />
                            {unit.responsible_name}
                          </p>
                        )}
                      </div>
                      <div className="flex flex-col gap-1">
                        <Button 
                          size="sm" 
                          variant="ghost" 
                          className="text-blue-600 hover:bg-blue-50"
                          onClick={(e) => { e.stopPropagation(); handleOpenChat(unit); }}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Unit Detail Modal */}
            <Dialog open={showUnitDetail} onOpenChange={setShowUnitDetail}>
              <DialogContent className="max-w-lg">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                      <Building2 className="w-5 h-5 text-blue-600" />
                    </div>
                    {selectedUnit?.name}
                  </DialogTitle>
                </DialogHeader>
                {selectedUnit && (
                  <div className="space-y-6 py-4">
                    <Badge className="text-sm">{selectedUnit.type}</Badge>
                    
                    <div className="space-y-4">
                      <h4 className="font-semibold text-slate-900 border-b pb-2">Informações do Responsável</h4>
                      
                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <User className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="text-sm text-slate-500">Nome</p>
                          <p className="font-medium">{selectedUnit.responsible_name || 'Não informado'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Shield className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="text-sm text-slate-500">Cargo</p>
                          <p className="font-medium">{selectedUnit.responsible_cargo || 'Não informado'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Mail className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="text-sm text-slate-500">E-mail</p>
                          <p className="font-medium">{selectedUnit.responsible_email || 'Não informado'}</p>
                        </div>
                      </div>

                      <div className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg">
                        <Phone className="w-5 h-5 text-slate-500" />
                        <div>
                          <p className="text-sm text-slate-500">Telefone</p>
                          <p className="font-medium">{selectedUnit.responsible_phone || 'Não informado'}</p>
                        </div>
                      </div>
                    </div>

                    <DialogFooter className="flex gap-2">
                      <Button variant="outline" onClick={() => setShowUnitDetail(false)}>
                        Fechar
                      </Button>
                      <Button 
                        className="bg-blue-600 hover:bg-blue-700"
                        onClick={() => { setShowUnitDetail(false); handleOpenChat(selectedUnit); }}
                      >
                        <MessageSquare className="w-4 h-4 mr-2" />
                        Iniciar Chat
                      </Button>
                    </DialogFooter>
                  </div>
                )}
              </DialogContent>
            </Dialog>

            {/* Unit Chat Modal */}
            <Dialog open={showUnitChat} onOpenChange={setShowUnitChat}>
              <DialogContent className="max-w-2xl h-[600px] flex flex-col">
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    <MessageSquare className="w-5 h-5 text-blue-600" />
                    Chat com {selectedUnit?.name}
                    <Badge variant="outline" className="ml-2 text-xs">Auditável</Badge>
                  </DialogTitle>
                </DialogHeader>
                
                <div className="flex-1 overflow-y-auto bg-slate-50 rounded-lg p-4 space-y-3">
                  {chatMessages.length === 0 ? (
                    <div className="text-center text-slate-400 py-10">
                      <MessageSquare className="w-12 h-12 mx-auto mb-3 opacity-50" />
                      <p>Nenhuma mensagem ainda</p>
                      <p className="text-sm">Inicie uma conversa com esta unidade</p>
                    </div>
                  ) : (
                    chatMessages.map((msg, i) => (
                      <div key={i} className={`flex ${msg.sender_name === user?.name || msg.sender_name === 'Você' ? 'justify-end' : 'justify-start'}`}>
                        <div className={`max-w-[70%] p-3 rounded-lg ${msg.sender_name === user?.name || msg.sender_name === 'Você' ? 'bg-blue-600 text-white' : 'bg-white border'}`}>
                          <p className="text-xs font-medium mb-1 opacity-70">{msg.sender_name}</p>
                          <p className="text-sm">{msg.message}</p>
                          <p className="text-xs mt-1 opacity-50">
                            {new Date(msg.created_at).toLocaleString('pt-BR')}
                          </p>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                <div className="flex gap-2 pt-4 border-t">
                  <Input
                    placeholder="Digite sua mensagem..."
                    value={chatMessage}
                    onChange={(e) => setChatMessage(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                    className="flex-1"
                  />
                  <Button onClick={handleSendMessage} className="bg-blue-600 hover:bg-blue-700">
                    <Send className="w-4 h-4" />
                  </Button>
                </div>
                
                <p className="text-xs text-slate-400 text-center">
                  Todas as mensagens são registradas para fins de auditoria
                </p>
              </DialogContent>
            </Dialog>
          </TabsContent>

          {/* RISK PANEL TAB */}
          <TabsContent value="risk" className="space-y-6">
            {riskPanel && (
              <>
                {/* Summary Cards */}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  {[
                    { label: 'Total de Atos', value: riskPanel.summary.total_acts, icon: FileText, color: 'blue' },
                    { label: 'Atos Ativos', value: riskPanel.summary.active_acts, icon: Clock, color: 'amber' },
                    { label: 'Atos Encerrados', value: riskPanel.summary.closed_acts, icon: CheckCircle, color: 'green' },
                    { label: 'Atos Críticos', value: riskPanel.summary.critical_acts, icon: AlertTriangle, color: 'red' },
                    { label: 'Atos Atrasados', value: riskPanel.summary.overdue_acts, icon: AlertTriangle, color: 'orange' }
                  ].map((item, i) => (
                    <Card key={i}>
                      <CardContent className="p-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-xl bg-${item.color}-50 flex items-center justify-center`}>
                            <item.icon className={`w-5 h-5 text-${item.color}-600`} />
                          </div>
                          <div>
                            <p className="text-2xl font-bold text-slate-900">{item.value}</p>
                            <p className="text-xs text-slate-500">{item.label}</p>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>

                {/* Charts */}
                <div className="grid lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle>Distribuição de Risco</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <PieChart>
                            <Pie
                              data={Object.entries(riskPanel.risk_distribution).map(([k, v]) => ({ name: k, value: v }))}
                              cx="50%"
                              cy="50%"
                              innerRadius={60}
                              outerRadius={100}
                              dataKey="value"
                              label={({ name, value }) => `${name}: ${value}`}
                            >
                              {Object.entries(riskPanel.risk_distribution).map((_, i) => (
                                <Cell key={i} fill={COLORS[i % COLORS.length]} />
                              ))}
                            </Pie>
                            <Tooltip />
                          </PieChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle>Curva de Prazos (30 dias)</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="h-64">
                        <ResponsiveContainer width="100%" height="100%">
                          <AreaChart data={riskPanel.deadline_curve}>
                            <defs>
                              <linearGradient id="colorCount" x1="0" y1="0" x2="0" y2="1">
                                <stop offset="5%" stopColor="#3B82F6" stopOpacity={0.3} />
                                <stop offset="95%" stopColor="#3B82F6" stopOpacity={0} />
                              </linearGradient>
                            </defs>
                            <XAxis dataKey="date" tick={{ fontSize: 10 }} />
                            <YAxis />
                            <Tooltip />
                            <Area type="monotone" dataKey="count" stroke="#3B82F6" fill="url(#colorCount)" />
                          </AreaChart>
                        </ResponsiveContainer>
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Ranking Table */}
                <Card>
                  <CardHeader>
                    <CardTitle>Ranking de Secretarias</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <table className="w-full">
                      <thead className="bg-slate-50">
                        <tr>
                          <th className="text-left p-3 text-sm font-semibold">Unidade</th>
                          <th className="text-center p-3 text-sm font-semibold">Total</th>
                          <th className="text-center p-3 text-sm font-semibold">Concluídos</th>
                          <th className="text-center p-3 text-sm font-semibold">Críticos</th>
                          <th className="text-center p-3 text-sm font-semibold">Performance</th>
                        </tr>
                      </thead>
                      <tbody>
                        {riskPanel.secretariat_ranking.slice(0, 10).map((r, i) => (
                          <tr key={i} className="border-b">
                            <td className="p-3 font-medium">{r.unit}</td>
                            <td className="p-3 text-center">{r.total}</td>
                            <td className="p-3 text-center text-green-600">{r.on_time}</td>
                            <td className="p-3 text-center text-red-600">{r.critical}</td>
                            <td className="p-3 text-center">
                              <Badge className={r.performance_rate >= 80 ? 'bg-green-50 text-green-700' : r.performance_rate >= 50 ? 'bg-amber-50 text-amber-700' : 'bg-red-50 text-red-700'}>
                                {r.performance_rate}%
                              </Badge>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </CardContent>
                </Card>
              </>
            )}
          </TabsContent>

          {/* REPORTS TAB */}
          <TabsContent value="reports" className="space-y-4">
            {/* Transition Report Card */}
            <Card className="border-2 border-amber-200 bg-gradient-to-r from-amber-50 to-orange-50">
              <CardContent className="p-6">
                <div className="flex items-start justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-2xl bg-amber-100 flex items-center justify-center">
                      <FileCheck className="w-7 h-7 text-amber-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg text-slate-900">Relatório de Transição de Governo</h3>
                      <p className="text-sm text-slate-600">Consolidação para passagem de mandato conforme Lei 14.133/2021</p>
                    </div>
                  </div>
                  <Button 
                    className="bg-amber-600 hover:bg-amber-700"
                    onClick={async () => {
                      try {
                        toast.info('Gerando PDF...');
                        const response = await api.get('/transition/report/pdf', { responseType: 'blob' });
                        const blob = new Blob([response.data], { type: 'application/pdf' });
                        const url = window.URL.createObjectURL(blob);
                        const a = document.createElement('a');
                        a.href = url;
                        a.download = `relatorio_transicao_${new Date().toISOString().split('T')[0]}.pdf`;
                        document.body.appendChild(a);
                        a.click();
                        window.URL.revokeObjectURL(url);
                        a.remove();
                        toast.success('PDF baixado com sucesso');
                      } catch (e) {
                        toast.error('Erro ao gerar PDF');
                        console.error(e);
                      }
                    }}
                  >
                    <Download className="w-4 h-4 mr-2" />Baixar PDF
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="grid md:grid-cols-2 gap-6">
              {[
                { type: 'tce', title: 'Relatório para TCE', desc: 'Tribunal de Contas do Estado', icon: FileCheck, color: 'blue' },
                { type: 'tcu', title: 'Relatório para TCU', desc: 'Tribunal de Contas da União', icon: Building2, color: 'indigo' },
                { type: 'mp', title: 'Relatório para MP', desc: 'Ministério Público', icon: Shield, color: 'purple' },
                { type: 'controladoria', title: 'Relatório Controladoria', desc: 'Controladoria Interna', icon: ClipboardList, color: 'emerald' },
                { type: 'executivo', title: 'Relatório Executivo', desc: 'Governador / Casa Civil', icon: TrendingUp, color: 'amber' }
              ].map((report) => (
                <Card key={report.type} className="hover:shadow-lg transition-shadow">
                  <CardContent className="p-6">
                    <div className="flex items-start justify-between">
                      <div className="flex items-center gap-4">
                        <div className={`w-14 h-14 rounded-2xl bg-${report.color}-50 flex items-center justify-center`}>
                          <report.icon className={`w-7 h-7 text-${report.color}-600`} />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg text-slate-900">{report.title}</h3>
                          <p className="text-sm text-slate-500">{report.desc}</p>
                        </div>
                      </div>
                      <Button onClick={() => handleGenerateReport(report.type)}>
                        <Download className="w-4 h-4 mr-2" />Baixar PDF
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </TabsContent>

          {/* AUDIT TAB */}
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle>Logs de Auditoria</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3 max-h-[600px] overflow-y-auto">
                  {auditLogs.map((log, i) => (
                    <div key={i} className="flex items-start gap-4 p-4 bg-slate-50 rounded-lg">
                      <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center flex-shrink-0">
                        <ClipboardList className="w-5 h-5 text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-slate-900">{log.user_info?.name || log.user_id}</span>
                          <Badge variant="outline" className="text-xs">{log.action}</Badge>
                        </div>
                        <p className="text-sm text-slate-500 mt-1">
                          {log.entity} {log.entity_id && `• ${log.entity_id}`}
                        </p>
                        <p className="text-xs text-slate-400 mt-1">
                          {new Date(log.created_at).toLocaleString('pt-BR')}
                          {log.ip_address && ` • IP: ${log.ip_address}`}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* ACCESS TAB */}
          <TabsContent value="access" className="space-y-4">
            {accessSummary && (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {[
                  { label: 'Total de Usuários', value: accessSummary.total_users, icon: Users, color: 'blue' },
                  { label: 'Usuários Ativos', value: accessSummary.active_users, icon: CheckCircle, color: 'green' },
                  { label: 'Usuários Inativos', value: accessSummary.inactive_users, icon: UserMinus, color: 'amber' },
                  { label: 'Nunca Acessaram', value: accessSummary.never_accessed, icon: AlertTriangle, color: 'red' }
                ].map((item, i) => (
                  <Card key={i}>
                    <CardContent className="p-6">
                      <div className="flex items-center gap-4">
                        <div className={`w-12 h-12 rounded-xl bg-${item.color}-50 flex items-center justify-center`}>
                          <item.icon className={`w-6 h-6 text-${item.color}-600`} />
                        </div>
                        <div>
                          <p className="text-3xl font-bold text-slate-900">{item.value}</p>
                          <p className="text-sm text-slate-500">{item.label}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
            {accessSummary && (
              <Card>
                <CardContent className="p-6">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-3xl font-bold text-slate-900">{accessSummary.recent_logins_7d}</p>
                      <p className="text-sm text-slate-500">Logins nos últimos 7 dias</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </AppLayout>
  );
};

export default AdminGovernoPage;
